/* ==========================================
   TEMA CUSTOM — theme.js
   Utilidades globales + componentes core
   ========================================== */

/* ---- Utilidades ---- */

/**
 * Atrapa el foco dentro de un elemento (modales, drawers).
 * Retorna función para liberar el foco.
 */
function trapFocus(element) {
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const focusableEls = element.querySelectorAll(FOCUSABLE);
  if (!focusableEls.length) return function() {};

  const first = focusableEls[0];
  const last = focusableEls[focusableEls.length - 1];

  function handler(e) {
    if (e.key !== 'Tab') return;
    if (e.shiftKey) {
      if (document.activeElement === first) {
        last.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    }
  }

  element.addEventListener('keydown', handler);
  first.focus();

  return function releaseFocus() {
    element.removeEventListener('keydown', handler);
  };
}

/**
 * Debounce: retrasa la ejecución hasta que pase el delay sin llamadas.
 */
function debounce(fn, delay) {
  if (delay === undefined) delay = 300;
  var timer;
  return function() {
    var context = this;
    var args = arguments;
    clearTimeout(timer);
    timer = setTimeout(function() { fn.apply(context, args); }, delay);
  };
}

/**
 * Throttle: ejecuta como máximo una vez cada `limit` ms.
 */
function throttle(fn, limit) {
  if (limit === undefined) limit = 100;
  var inThrottle = false;
  return function() {
    var context = this;
    var args = arguments;
    if (!inThrottle) {
      fn.apply(context, args);
      inThrottle = true;
      setTimeout(function() { inThrottle = false; }, limit);
    }
  };
}

/**
 * Fetch wrapper para Shopify AJAX API.
 */
async function shopifyFetch(url, options) {
  if (!options) options = {};
  var response = await fetch(url, Object.assign({}, options, {
    headers: Object.assign({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }, options.headers || {})
  }));
  if (!response.ok) throw new Error('HTTP ' + response.status);
  return response.json();
}


/* ---- Overlay global ---- */

var overlay = document.querySelector('[data-overlay]');

function showOverlay() {
  if (overlay) {
    overlay.hidden = false;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('drawer-open');
  }
}

function hideOverlay() {
  if (overlay) {
    overlay.hidden = true;
    document.body.style.overflow = '';
    document.body.classList.remove('drawer-open');
  }
}

if (overlay) {
  overlay.addEventListener('click', function() {
    document.dispatchEvent(new CustomEvent('drawer:close'));
    hideOverlay();
  });
}


/* ---- Header ---- */

(function() {
  var header = document.querySelector('[data-header]');
  if (!header) return;

  /* -- Sticky header -- */
  if (header.classList.contains('header--sticky')) {
    var sentinel = document.querySelector('[data-header-sentinel]');
    if (sentinel && 'IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function(entries) {
        header.classList.toggle('header--scrolled', !entries[0].isIntersecting);
      }, { threshold: 0 });
      observer.observe(sentinel);
    }
  }

  /* -- Mega menú (desktop) -- */
  var megaToggles = header.querySelectorAll('[data-mega-toggle]');
  var activeMega = null;
  var closeTimer = null;

  function openMega(toggle) {
    closeMega();
    var panel = toggle.nextElementSibling;
    if (!panel) return;
    toggle.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
    activeMega = { toggle: toggle, panel: panel };
  }

  function closeMega() {
    if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
    if (!activeMega) return;
    activeMega.toggle.setAttribute('aria-expanded', 'false');
    activeMega.panel.hidden = true;
    activeMega = null;
  }

  megaToggles.forEach(function(toggle) {
    var parent = toggle.closest('.header__nav-item');
    var panel = toggle.nextElementSibling;
    if (!panel) return;

    // Hover intent (desktop)
    parent.addEventListener('mouseenter', function() {
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
      openMega(toggle);
    });

    parent.addEventListener('mouseleave', function() {
      closeTimer = setTimeout(closeMega, 200);
    });

    // Click toggle
    toggle.addEventListener('click', function(e) {
      e.preventDefault();
      if (activeMega && activeMega.toggle === toggle) {
        closeMega();
      } else {
        openMega(toggle);
      }
    });

    // Keyboard: Enter/Space opens, Escape closes
    toggle.addEventListener('keydown', function(e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (activeMega && activeMega.toggle === toggle) {
          closeMega();
          toggle.focus();
        } else {
          openMega(toggle);
          // Focus first link inside mega menu
          var firstLink = panel.querySelector('a');
          if (firstLink) firstLink.focus();
        }
      }
      if (e.key === 'Escape') {
        closeMega();
        toggle.focus();
      }
    });
  });

  // Escape cierra mega menú desde cualquier contexto
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && activeMega) {
      var returnFocus = activeMega.toggle;
      closeMega();
      returnFocus.focus();
    }
  });


  /* -- Mobile menu (drawer) -- */
  var mobileMenu = document.querySelector('[data-mobile-menu]');
  var mobileToggle = document.querySelector('[data-mobile-menu-toggle]');
  var mobileClose = document.querySelector('[data-mobile-menu-close]');
  var releaseMobileFocus = null;

  function openMobileMenu() {
    if (!mobileMenu) return;
    mobileMenu.hidden = false;
    mobileMenu.classList.add('is-open');
    mobileToggle.setAttribute('aria-expanded', 'true');
    showOverlay();
    releaseMobileFocus = trapFocus(mobileMenu);
  }

  function closeMobileMenu() {
    if (!mobileMenu) return;
    mobileMenu.classList.remove('is-open');
    mobileMenu.hidden = true;
    mobileToggle.setAttribute('aria-expanded', 'false');
    hideOverlay();
    if (releaseMobileFocus) { releaseMobileFocus(); releaseMobileFocus = null; }
    mobileToggle.focus();
  }

  if (mobileToggle) {
    mobileToggle.addEventListener('click', function() {
      if (mobileMenu.hidden) {
        openMobileMenu();
      } else {
        closeMobileMenu();
      }
    });
  }

  if (mobileClose) {
    mobileClose.addEventListener('click', closeMobileMenu);
  }

  // Escape cierra mobile menu
  if (mobileMenu) {
    mobileMenu.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') closeMobileMenu();
    });
  }

  // Cerrar al escuchar evento global
  document.addEventListener('drawer:close', closeMobileMenu);

  /* -- Submenús mobile (accordion) -- */
  var subMenuToggles = mobileMenu ? mobileMenu.querySelectorAll('[data-submenu-toggle]') : [];
  subMenuToggles.forEach(function(btn) {
    btn.addEventListener('click', function() {
      var submenu = btn.nextElementSibling;
      var isOpen = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!isOpen));
      if (submenu) submenu.hidden = isOpen;
    });
  });


  /* -- Cart count sync -- */
  document.addEventListener('cart:updated', function(e) {
    var badges = document.querySelectorAll('[data-cart-count]');
    var count = (e.detail && e.detail.items_count) || 0;
    badges.forEach(function(badge) {
      badge.textContent = count;
      badge.hidden = count === 0;
    });
  });

})();

/* ==========================================
   Wishlist + Add-to-cart — event delegation global
   Funciona en todas las páginas sin importar qué
   sección renderice los botones.
   ========================================== */
(function () {

  /* ---- CARRITO ---- */
  document.addEventListener('click', async function (e) {
    var btn = e.target.closest('.arpie-add-to-cart');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    e.stopPropagation();
    var variantId = btn.dataset.variantId;
    if (!variantId) return;
    var orig = btn.textContent;
    btn.textContent = 'AGREGANDO...';
    btn.disabled = true;
    try {
      await shopifyFetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });
      var cartData = await shopifyFetch('/cart.js');
      btn.textContent = 'AGREGADO';
      document.dispatchEvent(new CustomEvent('cart:added', { detail: { items_count: cartData.item_count } }));
      setTimeout(function () { btn.textContent = orig; btn.disabled = false; }, 2000);
    } catch (err) {
      btn.textContent = orig;
      btn.disabled = false;
    }
  });

  /* ---- WISHLIST ---- */
  function getWishlist() {
    return JSON.parse(localStorage.getItem('arpie-wishlist') || '[]');
  }

  function isInWishlist(productId) {
    return getWishlist().some(function (item) {
      return typeof item === 'object' ? item.id === productId : String(item) === productId;
    });
  }

  function syncWishlistBtns() {
    document.querySelectorAll('.arpie-wishlist-btn').forEach(function (btn) {
      btn.classList.toggle('activo', isInWishlist(String(btn.dataset.productId)));
    });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.arpie-wishlist-btn');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var productId = String(btn.dataset.productId);
    var productHandle = btn.dataset.productHandle || '';
    var list = getWishlist();
    var idx = list.findIndex(function (item) {
      return typeof item === 'object' ? item.id === productId : String(item) === productId;
    });
    if (idx > -1) {
      list.splice(idx, 1);
      btn.classList.remove('activo');
    } else {
      list.push({ id: productId, handle: productHandle });
      btn.classList.add('activo');
    }
    localStorage.setItem('arpie-wishlist', JSON.stringify(list));
    document.dispatchEvent(new CustomEvent('wishlist:updated'));
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncWishlistBtns);
  } else {
    syncWishlistBtns();
  }

  document.addEventListener('collection:updated', syncWishlistBtns);

})();
